/**
 * 输入校验：PeerID / DID / 地址 / 助记词 / 金额。
 * 全部返回 boolean，UI 与服务层共用；服务层抛错请走 MeshLinkError。
 */

import { BIP39_WORD_INDEX } from '@/crypto/wordlist';
import { validateMnemonic as validateBip39 } from '@/crypto/mnemonic';

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
const TXHASH_RE = /^0x[0-9a-fA-F]{64}$/;
const DID_RE = /^did:dpb:0x[0-9a-fA-F]{40}$/;
/** PeerID：base58btc（libp2p identity multihash，通常以 12D3KooW 开头）。 */
const PEERID_RE = /^[1-9A-HJ-NP-Za-km-z]{46,56}$/;

/** 地址是否合法（0x + 40 hex）。 */
export function isAddress(value: string): boolean {
  return ADDRESS_RE.test(value);
}

/** txHash 是否合法（0x + 64 hex）。 */
export function isTxHash(value: string): boolean {
  return TXHASH_RE.test(value);
}

/** DID 是否形如 `did:dpb:0x` + 40 hex。 */
export function isDid(value: string): boolean {
  return DID_RE.test(value);
}

/** PeerID 格式是否合法。 */
export function isPeerId(value: string): boolean {
  return PEERID_RE.test(value);
}

/** CIDv1 是否合法（`b` + base32，raw codec + sha2-256 → 长度固定 59）。 */
export function isCid(value: string): boolean {
  return /^b[a-z2-7]{58}$/.test(value);
}

/** 归一化助记词输入：去空白、转小写、按空格切分。 */
export function normalizeMnemonic(input: string): string[] {
  return input.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/**
 * 助记词是否合法：长度 ∈ {12,15,18,21,24} + 每词在 BIP39 词表内 + **校验和正确**。
 * 只想知道「哪些词不认识」请用 `invalidMnemonicWords`（导入页的实时提示）。
 */
export function isValidMnemonic(input: string | string[]): boolean {
  const words = Array.isArray(input) ? input.map((w) => w.trim().toLowerCase()).filter(Boolean) : normalizeMnemonic(input);
  if (![12, 15, 18, 21, 24].includes(words.length)) return false;
  return validateBip39(words);
}

/** 返回助记词中不认识的单词（用于 UI 逐词标红）。 */
export function invalidMnemonicWords(input: string | string[]): string[] {
  const words = Array.isArray(input) ? input.map((w) => w.trim().toLowerCase()).filter(Boolean) : normalizeMnemonic(input);
  return words.filter((w) => !BIP39_WORD_INDEX.has(w));
}

/** 金额是否为合法的非负整数微单位串。 */
export function isValidMicroAmount(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

/** 金额是否为合法的 DPB 十进制串（最多 6 位小数）。 */
export function isValidDpbInput(value: string): boolean {
  return /^\d+(\.\d{1,6})?$/.test(value.trim());
}

/** 口令强度（0–4）：长度 ≥8 + 含大小写 + 含数字 + 含符号。 */
export function passwordStrength(password: string): number {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return score;
}

/** 昵称合法性：1–32 字符，去除首尾空格后非空。 */
export function isValidName(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length >= 1 && trimmed.length <= 32;
}
